import { useEffect, useRef, useState } from "react";
import { API, api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Buildings, IdentificationCard, MapPin, Envelope, Phone, Globe,
  Bank, Image as ImageIcon, Receipt, Check, X, UploadSimple, Trash, FloppyDisk,
  ShareNetwork, Copy, ArrowsClockwise, Eye, EyeSlash, EnvelopeSimple, PaperPlaneTilt,
  Tray, ShieldCheck,
} from "@phosphor-icons/react";
import DangerZone from "@/components/settings/DangerZone";
import ExternalApiKeyManager from "@/components/settings/ExternalApiKeyManager";
import ThirdPartyIntegrations from "@/components/settings/ThirdPartyIntegrations";
import IntakeRulesManager from "@/components/settings/IntakeRulesManager";
import { getToken } from "@/lib/authToken";
export default function CompanySettings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  const fileRef2 = useRef(null);

  const isAdmin = can.rootAdmin(user);
  const canDownloadAiTransparency = can.downloadAiTransparency(user);
  const isSeniorOnly = canDownloadAiTransparency && !isAdmin;

  const load = async () => {
    const r = await api.get("/settings/company");
    setData(r.data);
    setForm(r.data);
  };
  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  // Vista reducida para `gestor_senior`: sólo accede al informe de
  // transparencia IA. Sin datos del singleton company-settings (no tiene
  // permiso para leer/escribir el resto de la configuración).
  if (isSeniorOnly) {
    return (
      <div data-testid="settings-page-senior">
        <header className="h-14 border-b border-border bg-white px-6 flex items-center justify-between sticky top-0 z-20">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Configuración</div>
            <h1 className="font-display font-bold text-lg tracking-tight">Transparencia y cumplimiento</h1>
          </div>
        </header>
        <div className="p-6 max-w-4xl space-y-6">
          <AiTransparencySection />
        </div>
      </div>
    );
  }

  if (!isAdmin) return <Navigate to="/analytics" replace />;

  const dirty = data && form && JSON.stringify(data) !== JSON.stringify(form);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.logo_path;       // se actualiza por endpoint específico
      delete payload.logo_secondary_path;
      delete payload.updated_at;
      const r = await api.put("/settings/company", payload);
      setData(r.data);
      setForm(r.data);
      setSavedAt(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setErr("");
    try {
      const r = await api.post("/settings/company/logo", fd);
      setData(r.data);
      setForm(r.data);
    } catch (err2) {
      setErr(err2?.response?.data?.detail || "Error subiendo logo");
    }
    e.target.value = "";
  };

  const removeLogo = async () => {
    if (!confirm("¿Eliminar el logo actual?")) return;
    const r = await api.delete("/settings/company/logo");
    setData(r.data);
    setForm(r.data);
  };

  const onLogo2 = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setErr("");
    try {
      const r = await api.post("/settings/company/logo-secondary", fd);
      setData(r.data);
      setForm(r.data);
    } catch (err2) {
      setErr(err2?.response?.data?.detail || "Error subiendo logo secundario");
    }
    e.target.value = "";
  };

  const removeLogo2 = async () => {
    if (!confirm("¿Eliminar el logo secundario?")) return;
    const r = await api.delete("/settings/company/logo-secondary");
    setData(r.data);
    setForm(r.data);
  };

  if (!form) return null;

  const token = getToken();
  const logoUrl = data.logo_path
    ? `${API}/settings/company/logo?token=${token}&v=${data.updated_at || ""}`
    : null;
  const logo2Url = data.logo_secondary_path
    ? `${API}/settings/company/logo-secondary?token=${token}&v=${data.updated_at || ""}`
    : null;

  return (
    <div data-testid="settings-page">
      <header className="h-14 border-b border-border bg-white px-6 flex items-center justify-between sticky top-0 z-20">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted">Administración</div>
          <h1 className="font-display font-bold text-lg tracking-tight">Configuración del gabinete</h1>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && !dirty && (
            <span className="text-[11px] text-status-success-fg inline-flex items-center gap-1">
              <Check className="w-3 h-3" weight="bold" /> Guardado
            </span>
          )}
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="save-company"
            className="bg-foreground text-white text-xs px-4 py-1.5 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            <FloppyDisk className="w-3 h-3" weight="bold" />
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </header>

      <div className="p-6 max-w-4xl space-y-6">
        {err && (
          <div className="text-sm text-status-error-fg bg-status-error-bg border border-status-error-bd px-3 py-2 rounded">{err}</div>
        )}

        {/* === PRIVACIDAD / RGPD: % extracciones locales vs LLM === */}
        <PrivacyPanel />

        {/* === TRANSPARENCIA IA · AEPD / RGPD · Reglamento UE de IA === */}
        <AiTransparencySection />

        {/* === LOGOS === */}
        <Section title="Logotipos" icon={ImageIcon} desc="Aparecen en cabecera de informes y facturas. PNG/JPG/WEBP/SVG.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">Principal · Despacho</div>
              <div className="flex items-center gap-4">
                <div
                  className="w-44 h-32 bg-slate-50 border border-dashed border-border rounded-md flex items-center justify-center overflow-hidden flex-shrink-0"
                  data-testid="logo-preview"
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt="logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-xs text-muted">Sin logo</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => fileRef.current?.click()}
                    data-testid="logo-upload-btn"
                    className="text-xs border border-border hover:border-foreground rounded-md px-3 py-1.5 inline-flex items-center gap-1.5"
                  >
                    <UploadSimple className="w-3 h-3" weight="bold" />
                    {data.logo_path ? "Reemplazar" : "Subir logo"}
                  </button>
                  {data.logo_path && (
                    <button
                      onClick={removeLogo}
                      data-testid="logo-remove-btn"
                      className="text-xs text-status-error-fg hover:bg-status-error-bg rounded-md px-3 py-1.5 inline-flex items-center gap-1.5"
                    >
                      <Trash className="w-3 h-3" weight="bold" /> Eliminar
                    </button>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.svg,image/*"
                    onChange={onLogo}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">
                Secundario · Asociación / colegio (ej. APCAS)
              </div>
              <div className="flex items-center gap-4">
                <div
                  className="w-44 h-32 bg-slate-50 border border-dashed border-border rounded-md flex items-center justify-center overflow-hidden flex-shrink-0"
                  data-testid="logo2-preview"
                >
                  {logo2Url ? (
                    <img src={logo2Url} alt="logo asociación" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-xs text-muted">Sin logo</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => fileRef2.current?.click()}
                    data-testid="logo2-upload-btn"
                    className="text-xs border border-border hover:border-foreground rounded-md px-3 py-1.5 inline-flex items-center gap-1.5"
                  >
                    <UploadSimple className="w-3 h-3" weight="bold" />
                    {data.logo_secondary_path ? "Reemplazar" : "Subir logo"}
                  </button>
                  {data.logo_secondary_path && (
                    <button
                      onClick={removeLogo2}
                      data-testid="logo2-remove-btn"
                      className="text-xs text-status-error-fg hover:bg-status-error-bg rounded-md px-3 py-1.5 inline-flex items-center gap-1.5"
                    >
                      <Trash className="w-3 h-3" weight="bold" /> Eliminar
                    </button>
                  )}
                  <input
                    ref={fileRef2}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.svg,image/*"
                    onChange={onLogo2}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* === DATOS FISCALES === */}
        <Section title="Datos fiscales" icon={Buildings} desc="Identificación del gabinete como emisor de facturas e informes.">
          <Grid>
            <Field label="Nombre comercial">
              <Input value={form.name || ""} onChange={(v) => set("name", v)} placeholder="Dobilas Peritos" data-testid="set-name" />
            </Field>
            <Field label="Razón social">
              <Input value={form.razon_social || ""} onChange={(v) => set("razon_social", v)} placeholder="Dobilas, S.L." data-testid="set-razon" />
            </Field>
            <Field label="CIF" icon={IdentificationCard}>
              <Input value={form.cif || ""} onChange={(v) => set("cif", v.toUpperCase())} placeholder="B12345678" mono data-testid="set-cif" />
            </Field>
            <Field label="Pie de factura (notas legales)">
              <textarea
                value={form.pie_factura || ""}
                onChange={(e) => set("pie_factura", e.target.value)}
                placeholder="Texto que aparecerá al pie de cada factura."
                rows={2}
                data-testid="set-pie"
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
              />
            </Field>
          </Grid>
          <Field label="Dirección fiscal" icon={MapPin}>
            <Input value={form.direccion_fiscal || ""} onChange={(v) => set("direccion_fiscal", v)} placeholder="C/ Ejemplo 12, 1ºA" data-testid="set-direccion" />
          </Field>
          <Grid cols={3}>
            <Field label="Código postal">
              <Input value={form.codigo_postal || ""} onChange={(v) => set("codigo_postal", v)} placeholder="28013" data-testid="set-cp" />
            </Field>
            <Field label="Población">
              <Input value={form.poblacion || ""} onChange={(v) => set("poblacion", v)} placeholder="Madrid" data-testid="set-poblacion" />
            </Field>
            <Field label="Provincia">
              <Input value={form.provincia || ""} onChange={(v) => set("provincia", v)} placeholder="Madrid" data-testid="set-provincia" />
            </Field>
          </Grid>
        </Section>

        {/* === CONTACTO === */}
        <Section title="Contacto" icon={Envelope}>
          <Grid cols={3}>
            <Field label="Teléfono" icon={Phone}>
              <Input value={form.telefono || ""} onChange={(v) => set("telefono", v)} placeholder="+34 9XX XXX XXX" data-testid="set-telefono" />
            </Field>
            <Field label="Email" icon={Envelope}>
              <Input type="email" value={form.email || ""} onChange={(v) => set("email", v)} placeholder="contacto@dobilas.es" data-testid="set-email" />
            </Field>
            <Field label="Web" icon={Globe}>
              <Input value={form.web || ""} onChange={(v) => set("web", v)} placeholder="https://dobilas.es" data-testid="set-web" />
            </Field>
          </Grid>
        </Section>

        {/* === BANCARIO === */}
        <Section title="Datos bancarios" icon={Bank} desc="Aparecerán en las facturas para que el cliente pueda hacer la transferencia.">
          <Grid>
            <Field label="IBAN">
              <Input value={form.iban || ""} onChange={(v) => set("iban", v.toUpperCase())} placeholder="ES00 0000 0000 00 0000000000" mono data-testid="set-iban" />
            </Field>
            <Field label="SWIFT/BIC (opcional)">
              <Input value={form.swift || ""} onChange={(v) => set("swift", v.toUpperCase())} placeholder="BBVAESMM" mono data-testid="set-swift" />
            </Field>
          </Grid>
        </Section>

        {/* === FEDERACIÓN / CONEXIÓN CON GABINETES PARTNER === */}
        <FederationSection
          data={data}
          onRegenerated={(updated) => { setData(updated); setForm(updated); }}
        />

        {/* === SMTP / EMAIL SALIENTE === */}
        <SmtpSection />

        {/* === IMAP / EMAIL ENTRANTE === */}
        <ImapSection />

        {/* === PLANTILLAS DE EMAIL === */}
        <EmailTemplatesSection />

        {/* === FISCALIDAD === */}
        <Section title="Fiscalidad por defecto" icon={Receipt} desc="Porcentajes aplicados a nuevas facturas. Modificables si la legislación cambia.">
          <Grid cols={3}>
            <Field label="IVA por defecto (%)" hint="Tipo general 21%">
              <NumInput value={form.iva_pct ?? 21} onChange={(v) => set("iva_pct", v)} min={0} max={100} step={0.5} suffix="%" data-testid="set-iva" />
            </Field>
            <Field label="Retención IRPF (%)" hint="Habitual 7% para profesionales">
              <NumInput value={form.irpf_pct ?? 0} onChange={(v) => set("irpf_pct", v)} min={0} max={100} step={0.5} suffix="%" data-testid="set-irpf" />
            </Field>
            <Field label="Recargo de equivalencia (%)" hint="Sólo si aplica">
              <NumInput value={form.recargo_eq_pct ?? 0} onChange={(v) => set("recargo_eq_pct", v)} min={0} max={100} step={0.1} suffix="%" data-testid="set-recargo" />
            </Field>
          </Grid>
          <Grid>
            <Field label="Prefijo de número de factura" hint='ej. "DOB-" → DOB-2026-0001'>
              <Input value={form.factura_prefix || ""} onChange={(v) => set("factura_prefix", v)} placeholder="DOB-" mono data-testid="set-fact-prefix" />
            </Field>
            <Field label="Próximo número de factura" hint="Siguiente correlativo a emitir">
              <NumInput value={form.factura_next_number ?? 1} onChange={(v) => set("factura_next_number", Math.max(1, Math.floor(v)))} min={1} step={1} data-testid="set-fact-num" />
            </Field>
          </Grid>
        </Section>

        {/* === SLA INCIDENCIAS Y AMPLIACIONES === */}
        <Section
          title="SLA de incidencias y ampliaciones"
          icon={Receipt}
          desc="Tiempo (en horas) que tiene el destinatario para responder antes de que se considere vencida. Al crear una incidencia se puede pisar este valor con un SLA individual."
        >
          <Grid cols={2}>
            <Field label="SLA Incidencias (horas)" hint="Por defecto 24h">
              <NumInput
                value={form.incidencias_sla_h ?? 24}
                onChange={(v) => set("incidencias_sla_h", Math.max(1, Math.min(720, Math.floor(v || 1))))}
                min={1} max={720} step={1}
                suffix="h"
                data-testid="set-sla-incidencias"
              />
            </Field>
            <Field label="SLA Ampliaciones (horas)" hint="Por defecto 48h">
              <NumInput
                value={form.ampliaciones_sla_h ?? 48}
                onChange={(v) => set("ampliaciones_sla_h", Math.max(1, Math.min(720, Math.floor(v || 1))))}
                min={1} max={720} step={1}
                suffix="h"
                data-testid="set-sla-ampliaciones"
              />
            </Field>
          </Grid>
        </Section>

        {/* Zona de peligro (solo admin root). Aislada del flujo de guardado. */}
        {can.rootAdmin(user) && <ExternalApiKeyManager />}
        {can.rootAdmin(user) && <ThirdPartyIntegrations />}
        {can.rootAdmin(user) && <DangerZone />}

        {dirty && (
          <div className="sticky bottom-4 z-30">
            <div className="bg-white border border-foreground/30 shadow-lg rounded-md px-4 py-3 flex items-center justify-between">
              <span className="text-sm">Tienes cambios sin guardar</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setForm(data)} className="text-xs text-muted hover:text-foreground inline-flex items-center gap-1">
                  <X className="w-3 h-3" weight="bold" /> Descartar
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="save-company-bottom"
                  className="bg-foreground text-white text-xs px-4 py-1.5 rounded-md font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <Check className="w-3 h-3" weight="bold" />Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== IMAP / Email entrante =====
/**
 * Convierte cualquier respuesta de error (Axios, fetch, etc.) en un
 * string legible. Evita el viejo bug "Error: None" cuando el backend
 * devolvía `error: null` o detail: None.
 */
function describeError(err, fallback = "Error desconocido") {
  if (!err) return fallback;
  if (typeof err === "string" && err.trim() && err.trim().toLowerCase() !== "none") return err;
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim() && d.trim().toLowerCase() !== "none") return d;
  if (Array.isArray(d) && d.length) {
    // Errores de validación de FastAPI / Pydantic
    return d.map((it) => it?.msg || it?.loc?.join(".")).filter(Boolean).join("; ") || fallback;
  }
  const e2 = err?.response?.data?.error || err?.message;
  if (typeof e2 === "string" && e2.trim() && e2.trim().toLowerCase() !== "none") return e2;
  return fallback;
}

function ImapSection() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    imap_host: "", imap_port: 993, imap_user: "", imap_password: "",
    imap_folder: "INBOX", imap_folders: [], imap_use_ssl: true, imap_polling_enabled: false,
  });
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [polling, setPolling] = useState(false);

  const refresh = async () => {
    const r = await api.get("/settings/imap/status");
    setStatus(r.data);
    setForm((f) => ({
      ...f,
      imap_host: r.data.host || "",
      imap_port: r.data.port || 993,
      imap_user: r.data.user || "",
      imap_folder: r.data.folder || "INBOX",
      imap_folders: Array.isArray(r.data.folders) && r.data.folders.length ? r.data.folders : (r.data.folder ? [r.data.folder] : []),
      imap_use_ssl: r.data.use_ssl,
      imap_polling_enabled: r.data.polling_enabled,
      imap_password: "",
    }));
  };
  useEffect(() => { refresh(); }, []);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.imap_password) delete payload.imap_password;
      await api.put("/settings/imap", payload);
      toast.success("Configuración IMAP guardada");
      await refresh();
    } catch (e) {
      toast.error(describeError(e, "No se pudo guardar la configuración IMAP"));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/imap/test");
      if (r.data?.ok) toast.success(`Conexión OK · ${r.data.folder} · ${r.data.messages} mensajes`);
      else toast.error(describeError(r.data?.error, "No se pudo conectar al servidor IMAP"));
    } catch (e) { toast.error(describeError(e, "No se pudo conectar al servidor IMAP")); }
    finally { setTesting(false); }
  };

  const poll = async () => {
    setPolling(true);
    try {
      const r = await api.post("/settings/imap/poll");
      if (r.data?.ok) {
        toast.success(`Sincronización: ${r.data.indexed} email(s) indexado(s) · ${r.data.skipped} omitido(s)`);
        await refresh();
      } else {
        toast.error(describeError(r.data?.error, "No se pudo sincronizar"));
      }
    } catch (e) { toast.error(describeError(e, "No se pudo completar la operación")); }
    finally { setPolling(false); }
  };

  const intake = async () => {
    setPolling(true);
    try {
      // Procesa ALL en últimos 7 días, deduplica por Message-ID.
      // Esto evita perder correos que el usuario haya leído en Outlook.
      const r = await api.post("/settings/imap/intake?only_unseen=false&since_days=7");
      if (r.data?.ok) {
        const created = r.data.expedientes_created || 0;
        const matched = r.data.matched || 0;
        const scanned = r.data.scanned || 0;
        const folders = (r.data.folders_scanned || []).join(", ");
        if (created > 0) {
          toast.success(`✓ ${created} expediente(s) creado(s) desde correo${folders ? ` · carpetas: ${folders}` : ""}`);
        } else if (matched > 0) {
          toast.message(`${matched} correo(s) reconocido(s) en últimos 7 días — todos ya importados (dedup por Message-ID)`);
        } else if (scanned === 0) {
          toast.message(`Sin correos en últimos 7 días en ${folders || "las carpetas"}.`);
        } else {
          toast.message(`Sincronizado: ${scanned} correo(s) revisado(s) · ninguno coincide con reglas activas`);
        }
        await refresh();
      } else {
        toast.error(r.data?.error || "No se pudo importar");
      }
    } catch (e) { toast.error(describeError(e, "No se pudo completar la operación")); }
    finally { setPolling(false); }
  };

  // Importación retroactiva: recupera correos antiguos (ALL + dry_run)
  const [historyDays, setHistoryDays] = useState(7);
  const intakeHistory = async (dryRun) => {
    setPolling(true);
    try {
      const r = await api.post(
        `/settings/imap/intake?only_unseen=false&since_days=${historyDays}&dry_run=${dryRun ? "true" : "false"}`,
      );
      if (!r.data?.ok) {
        toast.error(r.data?.error || "No se pudo importar");
        return;
      }
      const matched = r.data.matched || 0;
      const created = r.data.expedientes_created || 0;
      const scanned = r.data.scanned || 0;
      if (dryRun) {
        toast.message(
          `Vista previa · ${matched} correo(s) coincidirían (${scanned} escaneados, últimos ${historyDays} días)`,
          { description: matched > 0 ? "Pulsa «Importar de verdad» para crear los expedientes." : "No hay nada que importar." },
        );
      } else if (created > 0) {
        toast.success(`✓ ${created} expediente(s) creado(s) desde correos antiguos (últimos ${historyDays} días)`);
        await refresh();
      } else {
        toast.message(`No se creó ningún expediente nuevo. Es probable que ya estuvieran todos importados (dedup por Message-ID).`);
      }
    } catch (e) {
      toast.error(describeError(e, "No se pudo completar la operación"));
    } finally {
      setPolling(false);
    }
  };

  // ─── Microsoft OAuth ───
  const [msBusy, setMsBusy] = useState(false);
  const connectMS = async () => {
    setMsBusy(true);
    try {
      const r = await api.get("/auth/microsoft/start");
      if (!r.data?.url) {
        toast.error("No se pudo iniciar el flujo OAuth de Microsoft");
        return;
      }
      // Abrir popup centrado
      const w = 560, h = 720;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2.5;
      const popup = window.open(
        r.data.url,
        "ms_oauth",
        `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`,
      );
      if (!popup) {
        toast.error("El navegador bloqueó el popup. Permítelo y reintenta.");
        return;
      }
      const handler = async (ev) => {
        if (ev.data?.source !== "ekspertium-ms-oauth") return;
        window.removeEventListener("message", handler);
        if (ev.data.success) {
          toast.success(`✓ ${ev.data.message}`);
          await refresh();
        } else {
          toast.error(`Microsoft: ${ev.data.message}`);
        }
      };
      window.addEventListener("message", handler);
      // Watchdog: si el popup se cierra sin enviar mensaje, refresca igualmente
      const watch = setInterval(() => {
        if (popup.closed) {
          clearInterval(watch);
          window.removeEventListener("message", handler);
          refresh();
        }
      }, 800);
    } catch (e) {
      toast.error(describeError(e, "No se pudo iniciar la conexión con Microsoft"));
    } finally {
      setMsBusy(false);
    }
  };

  const disconnectMS = async () => {
    if (!window.confirm("¿Desconectar la cuenta de Microsoft? La sincronización IMAP dejará de funcionar hasta que vuelvas a conectar.")) return;
    setMsBusy(true);
    try {
      await api.delete("/auth/microsoft/disconnect");
      toast.success("Cuenta de Microsoft desconectada");
      await refresh();
    } catch (e) {
      toast.error(describeError(e, "No se pudo desconectar"));
    } finally {
      setMsBusy(false);
    }
  };

  // Re-extraer expedientes en estado 'error' (tras fallo temporal del LLM)
  const [retryBusy, setRetryBusy] = useState(false);
  const retryFailedExtractions = async () => {
    setRetryBusy(true);
    try {
      const r = await api.post("/expedientes/retry-extraction/bulk");
      const n = r.data?.queued || 0;
      if (n === 0) {
        toast.message("No hay expedientes en estado «error» para reintentar");
      } else {
        toast.success(`✓ ${n} expediente(s) encolado(s) para reintento de extracción IA`);
      }
    } catch (e) {
      toast.error(describeError(e, "No se pudo reintentar"));
    } finally {
      setRetryBusy(false);
    }
  };

  return (
    <Section
      title="Email entrante (IMAP)"
      icon={Tray}
      desc="Indexa automáticamente las respuestas que recibas en los expedientes correspondientes. Se identifican por el sub-addressing del Reply-To: peritaciones+exp{id}@dobilas.es."
    >
      {/* ─── Bloque Microsoft OAuth (recomendado para Outlook/M365) ─── */}
      <div className={`rounded-md border p-3 ${status?.ms_connected ? "bg-sky-50 border-sky-200" : "bg-blue-50/50 border-blue-200"}`} data-testid="ms-oauth-block">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <svg viewBox="0 0 23 23" className="w-7 h-7 mt-0.5 shrink-0" aria-hidden="true">
              <rect x="1" y="1" width="10" height="10" fill="#F25022" />
              <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
              <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
              <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
            </svg>
            <div>
              <div className="text-sm font-semibold text-foreground">
                {status?.ms_connected ? "Outlook conectado" : "Conectar Outlook / Microsoft 365"}
              </div>
              <div className="text-xs text-muted mt-0.5 max-w-xl">
                {status?.ms_connected ? (
                  <>
                    Cuenta: <strong className="text-foreground">{status.ms_email}</strong>
                    {status.ms_connected_at && (
                      <> · Conectada el {new Date(status.ms_connected_at).toLocaleDateString()}</>
                    )}
                  </>
                ) : (
                  <>
                    Desde sept-2024 Microsoft <strong>desactivó</strong> el login básico de IMAP en cuentas Outlook personales y M365. Usa OAuth 2.0 para sincronizar emails. Requiere autorización única.
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {status?.ms_connected ? (
              <button
                type="button"
                onClick={disconnectMS}
                disabled={msBusy}
                data-testid="ms-disconnect-btn"
                className="text-xs border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 rounded-md px-3 py-2 font-semibold disabled:opacity-50"
              >
                Desconectar
              </button>
            ) : (
              <button
                type="button"
                onClick={connectMS}
                disabled={msBusy}
                data-testid="ms-connect-btn"
                className="text-xs bg-[#0078D4] hover:bg-[#106EBE] text-white rounded-md px-4 py-2 font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {msBusy ? "Abriendo..." : "🔗 Conectar Outlook"}
              </button>
            )}
          </div>
        </div>
      </div>

      {status && (
        <div className={`flex flex-wrap items-center gap-2 text-xs px-3 py-2 rounded-md border ${status.configured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}
             data-testid="imap-status">
          <span className={`w-2 h-2 rounded-full ${status.configured ? "bg-emerald-500" : "bg-rose-500"}`} />
          <strong>Estado:</strong> {status.configured ? "Configurado" : "Sin configurar"}
          {status.last_poll_at && (
            <>
              <span className="opacity-60">·</span>
              <span>Última sync: {new Date(status.last_poll_at).toLocaleString()} — {status.last_poll_result}</span>
            </>
          )}
        </div>
      )}

      <div className={`space-y-4 ${status?.ms_connected ? "opacity-50 pointer-events-none" : ""}`} data-testid="imap-classic-form">
      <div className="text-[11px] text-muted -mb-2">
        {status?.ms_connected
          ? "ℹ️ Outlook OAuth está activo. Estos campos quedan deshabilitados."
          : "Servidor IMAP clásico (login + contraseña). Para Outlook/M365 usa la conexión OAuth de arriba."}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Servidor IMAP">
          <input value={form.imap_host} onChange={(e) => setF("imap_host", e.target.value)} placeholder="outlook.office365.com" data-testid="imap-host" />
        </Field>
        <Field label="Puerto">
          <input type="number" value={form.imap_port} onChange={(e) => setF("imap_port", parseInt(e.target.value) || 993)} data-testid="imap-port" />
        </Field>
      </div>

      <Field label="Usuario / dirección de la cuenta">
        <input value={form.imap_user} onChange={(e) => setF("imap_user", e.target.value)} placeholder="peritaciones@dobilas.es" data-testid="imap-user" />
      </Field>

      <Field label="Contraseña" hint={status?.password_set ? "Hay una contraseña guardada. Déjalo vacío para mantenerla, o introduce una nueva para reemplazarla." : "Para Microsoft 365 / Google Workspace usa una App Password (16 caracteres)."}>
        <div className="flex gap-2">
          <input
            type={showPwd ? "text" : "password"}
            value={form.imap_password}
            onChange={(e) => setF("imap_password", e.target.value)}
            placeholder={status?.password_set ? "•••••••••••••••• (sin cambios)" : "App Password de 16 caracteres"}
            autoComplete="new-password"
            data-testid="imap-password"
            className="flex-1"
          />
          <button type="button" onClick={() => setShowPwd((v) => !v)} className="px-3 py-2 text-xs border border-border hover:border-foreground rounded-md inline-flex items-center gap-1.5 bg-white">
            {showPwd ? <EyeSlash className="w-3 h-3" weight="bold" /> : <Eye className="w-3 h-3" weight="bold" />}
            {showPwd ? "Ocultar" : "Ver"}
          </button>
        </div>
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Carpetas a sincronizar">
          <ImapFoldersPicker
            value={form.imap_folders || []}
            onChange={(arr) => setF("imap_folders", arr)}
            configured={!!status?.configured}
          />
        </Field>
        <div className="flex flex-col gap-2 pt-6">
          <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-muted">
            <input type="checkbox" checked={!!form.imap_use_ssl} onChange={(e) => setF("imap_use_ssl", e.target.checked)} data-testid="imap-ssl" />
            Conexión SSL/TLS (recomendado)
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-muted">
            <input type="checkbox" checked={!!form.imap_polling_enabled} onChange={(e) => setF("imap_polling_enabled", e.target.checked)} data-testid="imap-polling" />
            Habilitar sincronización
          </label>
        </div>
      </div>
      </div>

      <div className="flex flex-col md:flex-row gap-2 pt-3 border-t border-border">
        <button type="button" onClick={save} disabled={saving} data-testid="imap-save" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
          <FloppyDisk className="w-3.5 h-3.5" weight="bold" />
          {saving ? "Guardando..." : "Guardar configuración"}
        </button>
        <button type="button" onClick={test} disabled={testing || !status?.configured} data-testid="imap-test" className="text-xs border border-border hover:border-foreground rounded-md px-3 py-2 inline-flex items-center gap-1.5 bg-white disabled:opacity-50 disabled:cursor-not-allowed">
          <Check className="w-3.5 h-3.5" weight="bold" />
          {testing ? "Probando..." : "Probar conexión"}
        </button>
        <button type="button" onClick={poll} disabled={polling || !status?.configured} data-testid="imap-poll" className="text-xs border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed font-semibold">
          <ArrowsClockwise className={`w-3.5 h-3.5 ${polling ? "animate-spin" : ""}`} weight="bold" />
          {polling ? "Sincronizando..." : "Sincronizar ahora"}
        </button>
        <button type="button" onClick={intake} disabled={polling || !status?.configured} data-testid="imap-intake" className="text-xs border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed font-semibold" title="Procesa los correos nuevos de los últimos 7 días que coincidan con las reglas activas. Deduplica por Message-ID para no crear duplicados.">
          <ArrowsClockwise className={`w-3.5 h-3.5 ${polling ? "animate-spin" : ""}`} weight="bold" />
          {polling ? "Importando..." : "Auto-importar nuevos"}
        </button>
      </div>

      {/* Importación retroactiva de correos antiguos */}
      <div className="flex flex-wrap items-center gap-2 pt-2 text-xs">
        <span className="text-muted">Importar antiguos:</span>
        <select
          value={historyDays}
          onChange={(e) => setHistoryDays(parseInt(e.target.value))}
          className="text-xs border border-border rounded px-2 py-1 bg-white"
          data-testid="imap-history-days"
        >
          <option value={1}>últimas 24h</option>
          <option value={3}>últimos 3 días</option>
          <option value={7}>últimos 7 días</option>
          <option value={14}>últimos 14 días</option>
          <option value={30}>últimos 30 días</option>
          <option value={90}>últimos 90 días</option>
        </select>
        <button
          type="button"
          onClick={() => intakeHistory(true)}
          disabled={polling || !status?.configured}
          className="text-xs border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50 font-semibold"
          data-testid="imap-preview"
        >
          🔍 Vista previa
        </button>
        <button
          type="button"
          onClick={() => intakeHistory(false)}
          disabled={polling || !status?.configured}
          className="text-xs border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50 font-semibold"
          data-testid="imap-import-history"
        >
          ⬇ Importar de verdad
        </button>
        <span className="text-[10px] text-muted ml-2">Usa Message-ID para evitar duplicados.</span>
      </div>

      {/* Re-extraer expedientes que fallaron por error temporal del LLM */}
      <div className="flex flex-wrap items-center gap-2 pt-2 text-xs">
        <span className="text-muted">Reintento IA:</span>
        <button
          type="button"
          onClick={retryFailedExtractions}
          disabled={retryBusy}
          className="text-xs border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-800 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50 font-semibold"
          data-testid="retry-failed-extractions"
          title="Reintenta la extracción IA en los expedientes que fallaron por error temporal del LLM (budget, timeout, etc.)"
        >
          {retryBusy ? "Reintentando..." : "🔁 Re-extraer expedientes fallidos"}
        </button>
        <span className="text-[10px] text-muted ml-2">Para expedientes en estado «error» tras un fallo temporal del LLM.</span>
      </div>

      <details className="text-[11px] text-muted" data-testid="imap-intake-help">
        <summary className="cursor-pointer hover:text-foreground select-none font-semibold text-foreground text-sm py-2">⚙️ Reglas de auto-importación de siniestros</summary>
        <div className="mt-2 border-t border-border pt-3">
          <IntakeRulesManager msConnected={!!status?.ms_connected} />
        </div>
      </details>

      <details className="text-[11px] text-muted">
        <summary className="cursor-pointer hover:text-foreground select-none">¿Cómo funciona la asociación automática?</summary>
        <ol className="mt-2 pl-4 space-y-1 list-decimal">
          <li>Al enviar un email desde un expediente, el ERP pone <code className="font-mono bg-slate-100 px-1 rounded">peritaciones+exp{`{id}`}@dobilas.es</code> como Reply-To.</li>
          <li>Cuando el destinatario contesta, el email vuelve a esa dirección.</li>
          <li>Microsoft 365 / Gmail entregan los emails con sub-addressing (`+exp...`) en el INBOX normal del usuario.</li>
          <li>"Sincronizar ahora" lee los UNSEEN, extrae el id del expediente y lo indexa en su carpeta "Emails indexados". El mensaje se marca SEEN para no reprocesarse.</li>
          <li>Si un email no tiene la marca <code className="font-mono">+exp...</code> simplemente se omite (no se borra ni se mueve).</li>
        </ol>
      </details>
    </Section>
  );
}

// ===== Plantillas de email =====
const PLACEHOLDERS = ["numero", "asegurado", "aseguradora", "poliza", "siniestro", "firma"];

function EmailTemplatesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // index | null
  const [draft, setDraft] = useState({ name: "", subject: "", body: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/email-templates");
      setItems(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    try {
      await api.put("/settings/email-templates", { templates: next });
      setItems(next);
      toast.success("Plantillas actualizadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const addNew = () => {
    setDraft({ name: "", subject: "", body: "" });
    setEditing("new");
  };
  const edit = (i) => {
    setDraft({ ...items[i] });
    setEditing(i);
  };
  const cancel = () => { setEditing(null); setDraft({ name: "", subject: "", body: "" }); };
  const saveDraft = async () => {
    if (!draft.name.trim()) { toast.error("Nombre obligatorio"); return; }
    if (!draft.body.trim()) { toast.error("El cuerpo no puede estar vacío"); return; }
    let next;
    if (editing === "new") next = [...items, draft];
    else { next = [...items]; next[editing] = draft; }
    await persist(next);
    cancel();
  };
  const remove = async (i) => {
    if (!window.confirm(`¿Eliminar la plantilla "${items[i].name}"?`)) return;
    await persist(items.filter((_, idx) => idx !== i));
  };

  return (
    <Section
      title="Plantillas rápidas de email"
      icon={EnvelopeSimple}
      desc="Atajos para no escribir desde cero. Aparecen en el desplegable cuando envías un email desde un expediente. Soportan placeholders entre llaves dobles."
    >
      <div className="text-[11px] text-muted bg-amber-50 border border-amber-200 rounded-md px-3 py-2 leading-relaxed">
        <strong className="text-amber-900">Placeholders disponibles:</strong>{" "}
        {PLACEHOLDERS.map((p) => (
          <code key={p} className="font-mono bg-white px-1.5 py-0.5 rounded mr-1 border border-amber-200">{`{{${p}}}`}</code>
        ))}
        <span className="block mt-1 text-amber-800">Se sustituyen automáticamente con los datos del expediente al aplicar la plantilla.</span>
      </div>

      {loading ? (
        <p className="text-xs text-muted">Cargando…</p>
      ) : (
        <div className="space-y-2" data-testid="email-templates-list">
          {items.map((t, i) => (
            <div key={t.id || i} className="border border-border rounded-md px-3 py-2 flex items-start gap-3 hover:border-foreground/30">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-xs">{t.name}</div>
                {t.subject && <div className="text-[11px] text-muted truncate font-mono">{t.subject}</div>}
                <div className="text-[10px] text-muted mt-1 line-clamp-2 whitespace-pre-wrap">{t.body.slice(0, 140)}{t.body.length > 140 ? "…" : ""}</div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button type="button" onClick={() => edit(i)} data-testid={`tpl-edit-${i}`} className="text-muted hover:text-foreground p-1.5"><Receipt className="w-3.5 h-3.5" weight="bold" /></button>
                <button type="button" onClick={() => remove(i)} className="text-muted hover:text-rose-600 p-1.5"><Trash className="w-3.5 h-3.5" weight="bold" /></button>
              </div>
            </div>
          ))}
          <button type="button" onClick={addNew} data-testid="tpl-add" className="text-xs border border-dashed border-border hover:border-foreground rounded-md px-3 py-2 inline-flex items-center gap-1.5 font-semibold">
            + Añadir plantilla
          </button>
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={cancel}>
          <div className="bg-white rounded-lg w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="tpl-modal">
            <div className="px-6 pt-5 pb-3 border-b border-border flex items-center justify-between">
              <h2 className="font-display font-bold text-lg">{editing === "new" ? "Nueva plantilla" : "Editar plantilla"}</h2>
              <button onClick={cancel}><X className="w-4 h-4" weight="bold" /></button>
            </div>
            <div className="px-6 py-4 space-y-3 overflow-y-auto">
              <Field label="Nombre *">
                <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Solicitar presupuesto al reparador" data-testid="tpl-name" autoFocus />
              </Field>
              <Field label="Asunto (opcional, fallback al default del expediente)">
                <input value={draft.subject || ""} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} placeholder="[Expediente {{numero}}] Solicitud de presupuesto" data-testid="tpl-subject" className="font-mono" />
              </Field>
              <Field label="Mensaje *" hint="Usa {{numero}}, {{asegurado}}, {{aseguradora}}, {{poliza}}, {{siniestro}}, {{firma}}">
                <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={10} placeholder={"Hola,\n\nTe escribo en relación con el siniestro {{numero}}..."} data-testid="tpl-body" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 px-6 py-3 border-t border-border bg-surface/30">
              <button type="button" onClick={cancel} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
              <button type="button" onClick={saveDraft} data-testid="tpl-save" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

// ===== SMTP / Email saliente =====
function SmtpSection() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    smtp_host: "", smtp_port: 587, smtp_user: "",
    smtp_password: "", smtp_from: "", smtp_from_name: "Ekspertium",
    smtp_use_starttls: true, smtp_demo_recipients: "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const refresh = async () => {
    const r = await api.get("/settings/smtp/status");
    setStatus(r.data);
    setForm((f) => ({
      ...f,
      smtp_host: r.data.host || "",
      smtp_port: r.data.port || 587,
      smtp_user: r.data.user || "",
      smtp_from: r.data.from_addr || "",
      smtp_from_name: r.data.from_name || "Ekspertium",
      smtp_use_starttls: r.data.use_starttls,
      smtp_demo_recipients: r.data.demo_recipients || "",
      smtp_password: "",
    }));
  };
  useEffect(() => { refresh(); }, []);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Sólo enviar password si el usuario lo ha tecleado.
      const payload = { ...form };
      if (!payload.smtp_password) delete payload.smtp_password;
      await api.put("/settings/smtp", payload);
      toast.success("Configuración SMTP guardada");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const test = async () => {
    if (!testEmail.trim()) { toast.error("Indica un email para la prueba"); return; }
    setTesting(true);
    try {
      const r = await api.post("/settings/smtp/test", { to: testEmail.trim() });
      if (r.data?.ok) toast.success(`Correo de prueba enviado a ${testEmail}`);
      else if (r.data?.reason === "smtp-not-configured") toast.error("SMTP aún no está configurado. Guarda primero.");
      else toast.error(r.data?.error || "No se pudo enviar la prueba");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error de red");
    } finally { setTesting(false); }
  };

  const sourceLabel = {
    db: "guardado en este ERP",
    env: "leído del archivo .env",
    none: "sin configurar",
  }[status?.source] || "—";
  const stateColor = status?.configured ? "emerald" : "rose";

  return (
    <Section
      title="Email saliente (SMTP)"
      icon={PaperPlaneTilt}
      desc="Configura el servidor de correo para enviar facturas, invitaciones a partners, notificaciones de demos y emails desde los expedientes."
    >
      {/* Estado actual */}
      {status && (
        <div className={`flex flex-wrap items-center gap-2 text-xs px-3 py-2 rounded-md border ${status.configured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}
             data-testid="smtp-status">
          <span className={`w-2 h-2 rounded-full bg-${stateColor}-500`} />
          <strong>Estado:</strong> {status.configured ? "Configurado y funcionando" : "Sin configurar — los emails no se envían"}
          <span className="opacity-60">·</span>
          <span>Origen: {sourceLabel}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Servidor SMTP">
          <input value={form.smtp_host} onChange={(e) => setF("smtp_host", e.target.value)} placeholder="smtp.office365.com" data-testid="smtp-host" />
        </Field>
        <Field label="Puerto">
          <input type="number" value={form.smtp_port} onChange={(e) => setF("smtp_port", parseInt(e.target.value) || 587)} data-testid="smtp-port" />
        </Field>
      </div>

      <Field label="Usuario / dirección de la cuenta">
        <input value={form.smtp_user} onChange={(e) => setF("smtp_user", e.target.value)} placeholder="peritaciones@dobilas.es" data-testid="smtp-user" />
      </Field>

      <Field label="Contraseña" hint={status?.password_set ? "Hay una contraseña guardada. Déjalo vacío para mantenerla, o introduce una nueva para reemplazarla." : "Para Microsoft 365 / Google Workspace usa una App Password (16 caracteres), no la contraseña normal."}>
        <div className="flex gap-2">
          <input
            type={showPwd ? "text" : "password"}
            value={form.smtp_password}
            onChange={(e) => setF("smtp_password", e.target.value)}
            placeholder={status?.password_set ? "•••••••••••••••• (sin cambios)" : "App Password de 16 caracteres"}
            autoComplete="new-password"
            data-testid="smtp-password"
            className="flex-1"
          />
          <button type="button" onClick={() => setShowPwd((v) => !v)} className="px-3 py-2 text-xs border border-border hover:border-foreground rounded-md inline-flex items-center gap-1.5 bg-white" aria-label={showPwd ? "Ocultar" : "Ver"}>
            {showPwd ? <EyeSlash className="w-3 h-3" weight="bold" /> : <Eye className="w-3 h-3" weight="bold" />}
            {showPwd ? "Ocultar" : "Ver"}
          </button>
        </div>
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Remitente (From)">
          <input value={form.smtp_from} onChange={(e) => setF("smtp_from", e.target.value)} placeholder="peritaciones@dobilas.es" data-testid="smtp-from" />
        </Field>
        <Field label="Nombre del remitente">
          <input value={form.smtp_from_name} onChange={(e) => setF("smtp_from_name", e.target.value)} placeholder="Ekspertium" data-testid="smtp-from-name" />
        </Field>
      </div>

      <Field label="Destinatarios de notificación de demos" hint="Separados por comas. Reciben los leads que llegan desde la web pública.">
        <input value={form.smtp_demo_recipients} onChange={(e) => setF("smtp_demo_recipients", e.target.value)} placeholder="comercial@dobilas.es, mholgado@dobilas.es" data-testid="smtp-demo-recipients" />
      </Field>

      <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-muted">
        <input type="checkbox" checked={!!form.smtp_use_starttls} onChange={(e) => setF("smtp_use_starttls", e.target.checked)} data-testid="smtp-starttls" />
        Usar STARTTLS (recomendado para Microsoft 365 y Gmail)
      </label>

      <div className="flex flex-col md:flex-row gap-2 pt-3 border-t border-border">
        <button type="button" onClick={save} disabled={saving} data-testid="smtp-save" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
          <FloppyDisk className="w-3.5 h-3.5" weight="bold" />
          {saving ? "Guardando..." : "Guardar configuración"}
        </button>
        <div className="flex flex-1 gap-2">
          <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="tu@email.es" data-testid="smtp-test-to" className="flex-1 text-xs border border-border rounded-md px-3 py-2" />
          <button type="button" onClick={test} disabled={testing || !status?.configured} data-testid="smtp-test" className="text-xs border border-border hover:border-foreground rounded-md px-3 py-2 inline-flex items-center gap-1.5 bg-white disabled:opacity-50 disabled:cursor-not-allowed">
            <PaperPlaneTilt className="w-3.5 h-3.5" weight="bold" />
            {testing ? "Enviando..." : "Probar envío"}
          </button>
        </div>
      </div>

      <details className="text-[11px] text-muted">
        <summary className="cursor-pointer hover:text-foreground select-none">¿Cómo obtener una App Password de Microsoft 365?</summary>
        <ol className="mt-2 pl-4 space-y-1 list-decimal">
          <li>Abre <a href="https://mysignins.microsoft.com/security-info" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline">mysignins.microsoft.com/security-info</a> con la cuenta del remitente.</li>
          <li>Pulsa "Agregar método" → "Contraseña de aplicación" → ponle un nombre (ej: "Ekspertium").</li>
          <li>Copia la cadena de 16 caracteres y pégala arriba en "Contraseña". Sólo se muestra una vez.</li>
          <li>Guarda y pulsa "Probar envío" para verificar.</li>
        </ol>
        <p className="mt-2 text-[10px]">Requisitos: la cuenta debe tener MFA activo y el tenant debe permitir SMTP autenticado (Settings → Org settings → Modern authentication).</p>
      </details>
    </Section>
  );
}

// ===== Federación =====
function FederationSection({ data, onRegenerated }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const apiKey = data?.federation_api_key || "";
  const inboundUrl = `${API}/share/inbound/expediente`;
  const pingUrl = `${API}/share/ping`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("No se pudo copiar al portapapeles");
    }
  };

  const regenerate = async () => {
    if (!confirm(
      "¿Generar una nueva API Key?\n\n" +
      "La actual dejará de funcionar al instante. Tendrás que pasar la nueva " +
      "a TODOS los gabinetes partner que estuvieran conectados a este gabinete."
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/settings/company/regenerate-federation-key");
      onRegenerated(r.data);
      toast.success("Nueva API Key generada. Pásasela a tus partners.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo regenerar la clave");
    } finally {
      setBusy(false);
    }
  };

  const masked = apiKey ? apiKey.slice(0, 8) + "•".repeat(Math.max(0, apiKey.length - 12)) + apiKey.slice(-4) : "";

  return (
    <Section
      title="Federación con otros gabinetes"
      icon={ShareNetwork}
      desc="Comparte estos dos datos con tu gabinete partner. Su responsable los introduce en su ERP y queda conectado al tuyo. Puedes regenerar la clave en cualquier momento."
    >
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-[13px] leading-relaxed text-amber-900">
        <strong className="font-semibold">¿Cómo se comparte?</strong> Copia los dos campos y envíalos por correo, WhatsApp
        o el medio que prefieras al responsable del gabinete partner. Él los introduce
        en su sección de "Gabinetes partner" y a partir de ese momento podréis intercambiar expedientes.
      </div>

      <Field label="URL de tu gabinete (endpoint)" hint="El partner añadirá esta URL como la de tu instancia.">
        <div className="flex gap-2">
          <input
            value={inboundUrl}
            readOnly
            data-testid="fed-url"
            className="flex-1 border border-border rounded-md px-3 py-2 text-xs font-mono bg-slate-50 text-zinc-700 select-all"
          />
          <button
            type="button"
            onClick={() => copy(inboundUrl, "URL")}
            data-testid="fed-copy-url"
            className="px-3 py-2 text-xs border border-border hover:border-foreground rounded-md inline-flex items-center gap-1.5 bg-white"
          >
            <Copy className="w-3 h-3" weight="bold" /> Copiar
          </button>
        </div>
      </Field>

      <Field label="API Key del gabinete" hint="Cadena secreta. Trátala como una contraseña: no la pegues en sitios públicos.">
        <div className="flex gap-2">
          <input
            value={show ? apiKey : masked}
            readOnly
            data-testid="fed-key"
            className="flex-1 border border-border rounded-md px-3 py-2 text-xs font-mono bg-slate-50 text-zinc-700 select-all"
          />
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            data-testid="fed-toggle-show"
            className="px-3 py-2 text-xs border border-border hover:border-foreground rounded-md inline-flex items-center gap-1.5 bg-white"
            aria-label={show ? "Ocultar" : "Mostrar"}
          >
            {show ? <EyeSlash className="w-3 h-3" weight="bold" /> : <Eye className="w-3 h-3" weight="bold" />}
            {show ? "Ocultar" : "Mostrar"}
          </button>
          <button
            type="button"
            onClick={() => copy(apiKey, "API Key")}
            data-testid="fed-copy-key"
            className="px-3 py-2 text-xs border border-border hover:border-foreground rounded-md inline-flex items-center gap-1.5 bg-white"
          >
            <Copy className="w-3 h-3" weight="bold" /> Copiar
          </button>
        </div>
      </Field>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2 border-t border-border">
        <div className="text-[11px] text-muted">
          ¿Has compartido la clave con quien no debías? Genera una nueva ahora.
        </div>
        <button
          type="button"
          onClick={regenerate}
          disabled={busy}
          data-testid="fed-regenerate-btn"
          className="text-xs border border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100 disabled:opacity-50 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 font-semibold"
        >
          <ArrowsClockwise className="w-3 h-3" weight="bold" />
          {busy ? "Regenerando…" : "Regenerar API Key"}
        </button>
      </div>

      <details className="text-[11px] text-muted mt-2">
        <summary className="cursor-pointer hover:text-foreground select-none">
          Comprobar conexión / endpoint avanzado
        </summary>
        <div className="mt-2 pl-3 border-l-2 border-amber-200 space-y-1">
          <p>El partner puede verificar que la conexión funciona haciendo:</p>
          <code className="block bg-slate-100 px-2 py-1.5 rounded font-mono text-[10px] break-all">
            curl -H "X-Partner-Token: TU_API_KEY" {pingUrl}
          </code>
        </div>
      </details>
    </Section>
  );
}

// ===== UI helpers =====
function Section({ title, icon: Icon, desc, children }) {
  return (
    <section className="bg-white border border-border rounded-md p-5 space-y-3">
      <div>
        <h2 className="font-display font-bold text-sm tracking-tight inline-flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-violet-600" weight="duotone" />}{title}
        </h2>
        {desc && <p className="text-[11px] text-muted mt-0.5">{desc}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Grid({ cols = 2, children }) {
  return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-3`}>{children}</div>;
}

function Field({ label, icon: Icon, hint, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest font-semibold text-muted mb-1 inline-flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" weight="bold" />}{label}
      </label>
      {children}
      {hint && <p className="text-[10px] text-muted mt-1">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, type = "text", mono, ...rest }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground ${mono ? "font-mono" : ""}`}
      {...rest}
    />
  );
}

function NumInput({ value, onChange, suffix, ...rest }) {
  return (
    <div className="relative">
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
        {...rest}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted pointer-events-none">{suffix}</span>
      )}
    </div>
  );
}


// ============================================================
// IMAP FOLDERS PICKER — Selector multi de carpetas IMAP del servidor.
// Lista automática de carpetas reales (vía /api/settings/imap/folders)
// + posibilidad de añadir manualmente nombres que no aparezcan.
// ============================================================
function ImapFoldersPicker({ value = [], onChange, configured }) {
  const [available, setAvailable] = useState(null); // null=cargando, []=vacío
  const [error, setError] = useState(null);
  const [manualInput, setManualInput] = useState("");

  useEffect(() => {
    if (!configured) { setAvailable([]); return; }
    let alive = true;
    api.get("/settings/imap/folders")
      .then((r) => alive && setAvailable(r.data?.folders || []))
      .catch((e) => alive && setError(e?.response?.data?.detail || "No se pudo listar carpetas"));
    return () => { alive = false; };
  }, [configured]);

  const toggle = (name) => {
    if (value.includes(name)) onChange(value.filter((v) => v !== name));
    else onChange([...value, name]);
  };

  const addManual = () => {
    const n = manualInput.trim();
    if (!n || value.includes(n)) return;
    onChange([...value, n]);
    setManualInput("");
  };

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="imap-folders-selected">
          {value.map((f) => (
            <span key={f} className="inline-flex items-center gap-1 bg-orange-100 text-orange-800 text-[11px] font-semibold px-2 py-0.5 rounded-full">
              {f}
              <button
                onClick={() => toggle(f)}
                className="hover:text-orange-900"
                data-testid={`imap-folder-remove-${f}`}
                type="button"
              >×</button>
            </span>
          ))}
        </div>
      )}

      {available === null && configured && (
        <div className="text-[11px] text-muted">Cargando carpetas del servidor…</div>
      )}
      {error && <div className="text-[11px] text-red-600">{error}</div>}

      {Array.isArray(available) && available.length > 0 && (
        <div className="max-h-44 overflow-y-auto border border-border rounded-md p-2 text-[12px] space-y-0.5 bg-white">
          {available.map((f) => (
            <label key={f} className="flex items-center gap-2 px-1 py-0.5 hover:bg-stone-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={value.includes(f)}
                onChange={() => toggle(f)}
                data-testid={`imap-folder-${f}`}
              />
              <span className="truncate">{f}</span>
            </label>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <input
          value={manualInput}
          onChange={(e) => setManualInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addManual())}
          placeholder="Añadir carpeta manualmente (ej. AUTOAGENDA)"
          className="flex-1 text-[12px] border border-border rounded-md px-2 py-1"
          data-testid="imap-folder-manual-input"
        />
        <button
          type="button"
          onClick={addManual}
          className="text-[12px] px-3 py-1 border border-border rounded-md hover:bg-stone-50"
          data-testid="imap-folder-manual-add"
        >Añadir</button>
      </div>
      <p className="text-[10px] text-muted">
        Solo se procesarán emails de las carpetas seleccionadas. Las reglas con su propia carpeta solo se ejecutan si ésa está en la lista.
      </p>
    </div>
  );
}


// =================================================================
// PrivacyPanel — Badge RGPD + estadísticas de extracción local vs LLM
// =================================================================
function PrivacyPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/admin/extraction/stats?days=7")
      .then((r) => { if (alive) setStats(r.data); })
      .catch(() => { if (alive) setStats(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!stats) return null;
  const { totals, pct_local, by_aseguradora } = stats;
  const t = totals || {};
  // Badge tone: verde si ≥95%, ámbar 70-94%, rojo <70% (o sin datos)
  const tone = t.total === 0
    ? "neutral"
    : pct_local >= 95 ? "ok"
    : pct_local >= 70 ? "warn"
    : "alert";
  const toneCls = {
    ok:      "bg-emerald-50 border-emerald-300 text-emerald-900",
    warn:    "bg-amber-50 border-amber-300 text-amber-900",
    alert:   "bg-rose-50 border-rose-300 text-rose-900",
    neutral: "bg-slate-50 border-slate-200 text-slate-700",
  }[tone];

  return (
    <section
      data-testid="privacy-panel"
      className={`border rounded-lg overflow-hidden ${toneCls}`}
    >
      <header className="flex items-center gap-3 px-5 py-3 border-b border-current/15">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white/60 border border-current/20">
          <ShieldCheck className="w-5 h-5" weight="fill" />
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="font-display font-bold text-base tracking-tight">RGPD · Privacidad de extracción</h2>
            {tone === "ok" && (
              <span
                data-testid="privacy-badge-ok"
                className="text-[10px] uppercase font-bold tracking-widest bg-white/80 px-2 py-0.5 rounded border border-current/30"
              >
                RGPD-First
              </span>
            )}
          </div>
          <p className="text-[11.5px] opacity-80">
            {t.total === 0
              ? "Sin actividad en los últimos 7 días. Los datos se actualizan automáticamente al procesar partes."
              : `Últimos 7 días · ${t.total} parte${t.total === 1 ? "" : "s"} procesado${t.total === 1 ? "" : "s"}`}
          </p>
        </div>
      </header>

      {t.total > 0 && (
        <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
          {/* Big number */}
          <div className="flex flex-col items-center justify-center" data-testid="privacy-pct-local">
            <div className="text-5xl font-bold tabular-nums leading-none">{pct_local}%</div>
            <div className="text-[10px] uppercase tracking-widest opacity-70 mt-1">100% local</div>
            <div className="mt-3 text-[11px] opacity-80 text-center">
              {t.local} sin LLM · {t.llm_text + t.llm_multimodal} via Gemini · {t.manual} manual
            </div>
          </div>

          {/* Breakdown por aseguradora */}
          <div className="space-y-1.5" data-testid="privacy-by-aseguradora">
            <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold mb-1">
              Por aseguradora
            </div>
            {by_aseguradora.slice(0, 6).map((row) => {
              const n = row.local + row.llm + (row.manual || 0);
              const pct = n > 0 ? Math.round((row.local / n) * 100) : 0;
              return (
                <div key={row.name} className="flex items-center gap-2 text-[11.5px]">
                  <span className="w-24 truncate font-medium">{row.name}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-white/40 overflow-hidden">
                    <div
                      className="h-full bg-current opacity-70"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="tabular-nums w-10 text-right opacity-80">{pct}%</span>
                  <span className="tabular-nums w-12 text-right opacity-60 text-[10.5px]">{n}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <footer className="px-5 py-2 border-t border-current/15 text-[10.5px] opacity-75 flex items-center justify-between">
        <span>
          Los datos personales de partes se procesan localmente con parsers regex. Solo cuando ninguna plantilla coincide se recurre a Gemini (DPA firmado).
        </span>
      </footer>
    </section>
  );
}


// =================================================================
// AiTransparencySection — Botón para descargar el Informe de
// Transparencia sobre Uso de IA (admin + gestor_senior).
// =================================================================
function AiTransparencySection() {
  const [busy, setBusy] = useState(false);
  const [lastDownload, setLastDownload] = useState(null);
  const [error, setError] = useState("");

  const handleDownload = async () => {
    setBusy(true);
    setError("");
    try {
      const tok = getToken();
      const res = await fetch(
        `${API}/compliance/ai-transparency-report.pdf`,
        { headers: tok ? { Authorization: `Bearer ${tok}` } : {} },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const docId = res.headers.get("x-document-id") || "";
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date();
      const stamp = `${String(ts.getFullYear())}${String(ts.getMonth() + 1).padStart(2, "0")}${String(ts.getDate()).padStart(2, "0")}_${String(ts.getHours()).padStart(2, "0")}${String(ts.getMinutes()).padStart(2, "0")}`;
      a.download = `Transparencia_IA_${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setLastDownload({ at: new Date(), id: docId });
      toast.success("Informe de transparencia IA descargado");
    } catch (e) {
      setError(e?.message || "Error descargando el informe");
      toast.error("No se pudo descargar el informe");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="ai-transparency-panel"
      className="bg-white border border-border rounded-lg overflow-hidden"
    >
      <header className="flex items-start gap-3 px-5 py-3 border-b border-border bg-slate-50">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white border border-border text-foreground">
          <ShieldCheck className="w-5 h-5" weight="fill" />
        </span>
        <div className="flex-1">
          <h2 className="font-display font-bold text-base tracking-tight">
            Transparencia IA · AEPD / RGPD
          </h2>
          <p className="text-[11.5px] text-muted leading-snug">
            Informe oficial con el inventario de usos de IA, datos enviados al
            modelo, base legal del tratamiento y métricas reales de los
            últimos 30 días. Pensado para auditorías AEPD y para el
            Reglamento (UE) 2024/1689 sobre IA.
          </p>
        </div>
      </header>
      <div className="px-5 py-4 space-y-3 text-[12.5px] text-foreground">
        <ul className="list-disc list-inside space-y-1 text-[11.5px] text-muted leading-snug">
          <li>10 flujos de IA documentados (modelo, datos, retención, base legal).</li>
          <li>Proveedor y sub-encargados (Google Gemini · Emergent · DPA, ubicación de servidores, transferencias internacionales).</li>
          <li>Datos que <strong>NO</strong> se envían al modelo (RGPD-First).</li>
          <li>Métricas reales de procesamiento local vs LLM en los últimos 30 días.</li>
          <li>Cabecera con logo del gabinete, fecha y hora exactas, usuario solicitante e identificador único de descarga.</li>
        </ul>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            type="button"
            onClick={handleDownload}
            disabled={busy}
            data-testid="ai-transparency-download-btn"
            className="inline-flex items-center gap-2 px-3.5 h-9 rounded-md bg-foreground text-white text-[12.5px] font-semibold hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UploadSimple className="w-4 h-4 rotate-180" weight="bold" />
            {busy ? "Generando informe…" : "Descargar PDF"}
          </button>
          {lastDownload && (
            <span
              className="text-[11px] text-muted"
              data-testid="ai-transparency-last-download"
            >
              Última descarga el{" "}
              {lastDownload.at.toLocaleDateString("es-ES", {
                day: "2-digit", month: "2-digit", year: "numeric",
              })}{" "}
              a las{" "}
              {lastDownload.at.toLocaleTimeString("es-ES", {
                hour: "2-digit", minute: "2-digit",
              })}
              {lastDownload.id && ` · ID ${lastDownload.id}`}
            </span>
          )}
        </div>
        {error && (
          <div
            className="text-[11px] text-status-error-fg"
            data-testid="ai-transparency-error"
          >
            {error}
          </div>
        )}
      </div>
      <footer className="px-5 py-2 border-t border-border text-[10.5px] text-muted">
        Cada descarga queda registrada en la auditoría interna del gabinete
        con la fecha, hora, usuario y rol que solicitó el informe.
      </footer>
    </section>
  );
}
